import { getAceHeroItemNames, aceHeroItemSlots } from "./ace-hero-item.get-names";
import { h, forceUpdate } from "@stencil/core";
import { getInlineCssColor, isMobile, mergeClassNames, ResizeListener } from "../../utils";
export class AceHeroItem {
    constructor() {
        /**
         * If we have no image but a background color then pass it here.
         */
        this.backgroundColor = null;
        this.allowInfoBlocks = ['general', 'encapsulated', 'full-background'];
        this.doNotAllowDescription = ['game'];
        this.allowControls = ['general', 'encapsulated', 'full-background'];
        this.mustHaveStickyCtas = ['general', 'encapsulated'];
        this.hasOverlay = ['basic', 'full-background', 'game'];
    }
    componentWillLoad() {
        var _a;
        this.names = getAceHeroItemNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        if (this.backgroundColor) {
            this._backgroundColor = getInlineCssColor(this.backgroundColor);
        }
        if (this.backgroundSrc) {
            this._backgroundSrc = this.backgroundSrc;
        }
        if (this.mustHaveStickyCtas.includes(this.type)) {
            ResizeListener.getInstance().subscribe(() => {
                forceUpdate(this);
            });
        }
    }
    componentDidRender() {
        this.handleHeroItemDidRender();
    }
    disconnectedCallback() {
        ResizeListener.getInstance().unsubscribe(() => {
            forceUpdate(this);
        });
    }
    changeBgSrc() {
        this._backgroundSrc = this.backgroundSrc;
    }
    changeBgColor() {
        this._backgroundColor = this.backgroundColor;
    }
    handleNextClick() {
        this.nextHeroItem.emit({ currentHeroItemIndex: this.index, heroSliderKey: this.heroSliderKey });
    }
    handlePrevClick() {
        this.prevHeroItem.emit({ currentHeroItemIndex: this.index, heroSliderKey: this.heroSliderKey });
    }
    handleHeroItemDidRender() {
        this.heroItemDidRender.emit();
    }
    renderBackgroundVisual() {
        if (!this._backgroundSrc && !this._backgroundColor && !this.backgroundPlaceholderSrc) {
            return;
        }
        const hasOverlay = this.hasOverlay.includes(this.type) && this.overlay !== false;
        const backgroundClasses = {
            [this.names.CLASS.BG]: true,
            [this.names.CLASS.BG____WITH_OVERLAY]: hasOverlay,
        };
        if (this._backgroundSrc || this.backgroundPlaceholderSrc) {
            const imgAttrs = Object.assign(Object.assign(Object.assign(Object.assign({}, (this._backgroundSrc && { 'src': this._backgroundSrc })), { 'full-width': 'true', 'full-height': 'true' }), (this.backgroundAlt && { 'alt': this.backgroundAlt })), (this.backgroundPlaceholderSrc && { 'placeholder-src': this.backgroundPlaceholderSrc }));
            return (h("div", { class: mergeClassNames(backgroundClasses) }, h("ace-image", Object.assign({}, imgAttrs))));
        }
        return h("div", { class: mergeClassNames(backgroundClasses), style: { backgroundColor: this._backgroundColor } });
    }
    renderControls(forMobile = false) {
        if (this.allowControls.includes(this.type) && this.heroSliderKey && this.index !== undefined) {
            const finalClass = mergeClassNames({
                [this.names.CLASS.CONTENT____ENCAPSULATED__CONTROLS_BLOCK]: this.type === 'encapsulated',
                [this.names.CLASS.CONTENT____GAME__CONTROLS_BLOCK]: this.type === 'game',
                [this.names.CLASS.CONTENT____BASIC__CONTROLS_BLOCK]: this.type === 'basic',
                [this.names.CLASS.CONTENT____GENERAL__CONTROLS_BLOCK]: this.type === 'general',
                [this.names.CLASS.CONTENT____FULL_BACKGROUND__CONTROLS_BLOCK]: this.type === 'full-background',
                [this.names.CLASS.CONTENT____ENCAPSULATED__CONTROLS_BLOCK____MOBILE]: this.type === 'encapsulated' && forMobile,
                [this.names.CLASS.CONTENT____GAME__CONTROLS_BLOCK____MOBILE]: this.type === 'game' && forMobile,
                [this.names.CLASS.CONTENT____BASIC__CONTROLS_BLOCK____MOBILE]: this.type === 'basic' && forMobile,
                [this.names.CLASS.CONTENT____GENERAL__CONTROLS_BLOCK____MOBILE]: this.type === 'general' && forMobile,
                [this.names.CLASS.CONTENT____FULL_BACKGROUND__CONTROLS_BLOCK____MOBILE]: this.type === 'full-background' && forMobile,
            });
            return (h("div", { class: finalClass }, h("ace-button", { "icon-name": 'arrow-left', type: 'tertiary', class: this.names.CLASS.LEFT_ARROW, onClick: () => this.handlePrevClick() }), h("ace-button", { "icon-name": 'arrow-right', type: 'tertiary', class: this.names.CLASS.RIGHT_ARROW, onClick: () => this.handleNextClick() })));
        }
    }
    /**
     * @private
     */
    renderTagsSlot() {
        const tagsSlot = this.hostElement.querySelector(`[slot=${aceHeroItemSlots.TAGS}]`);
        if (tagsSlot && tagsSlot.outerHTML) {
            return (h("div", { class: mergeClassNames({
                    [this.names.CLASS.CONTENT____BASIC__TAGS_BLOCK]: this.type === 'basic',
                    [this.names.CLASS.CONTENT____ENCAPSULATED__TAGS_BLOCK]: this.type === 'encapsulated',
                    [this.names.CLASS.CONTENT____GAME__TAGS_BLOCK]: this.type === 'game',
                    [this.names.CLASS.CONTENT____GENERAL__TAGS_BLOCK]: this.type === 'general',
                    [this.names.CLASS.CONTENT____FULL_BACKGROUND__TAGS_BLOCK]: this.type === 'full-background',
                }) }, h("slot", { name: aceHeroItemSlots.TAGS })));
        }
        return null;
    }
    /**
     * @private
     */
    renderInfoSlot() {
        if (this.allowInfoBlocks.includes(this.type)) {
            const infoSlot = this.hostElement.querySelector(`[slot=${aceHeroItemSlots.INFO}]`);
            if (infoSlot && infoSlot.outerHTML) {
                return (h("div", { class: mergeClassNames({
                        [this.names.CLASS.CONTENT____BASIC__INFO_BLOCK]: this.type === 'basic',
                        [this.names.CLASS.CONTENT____ENCAPSULATED__INFO_BLOCK]: this.type === 'encapsulated',
                        [this.names.CLASS.CONTENT____GAME__INFO_BLOCK]: this.type === 'game',
                        [this.names.CLASS.CONTENT____GENERAL__INFO_BLOCK]: this.type === 'general',
                        [this.names.CLASS.CONTENT____FULL_BACKGROUND__INFO_BLOCK]: this.type === 'full-background',
                    }) }, h("slot", { name: aceHeroItemSlots.INFO })));
            }
        }
        return null;
    }
    /**
     * @private
     */
    renderLabelSlot() {
        const labelSlot = this.hostElement.querySelector(`[slot=${aceHeroItemSlots.LABEL}]`);
        if (labelSlot && labelSlot.outerHTML) {
            return h("slot", { name: aceHeroItemSlots.LABEL });
        }
        return null;
    }
    /**
     * @private
     */
    renderTextBlock() {
        const descSlot = this.hostElement.querySelector(`[slot=${aceHeroItemSlots.DESCRIPTION}]`);
        return (h("div", { class: this.names.CLASS.CONTENT__TEXT_BLOCK }, this.renderLabelSlot(), h("div", { class: this.names.CLASS.CONTENT__TEXT_BLOCK__HEADLINE_DESC_BLOCK }, h("slot", { name: aceHeroItemSlots.HEADLINE }), this.doNotAllowDescription.includes(this.type) || !descSlot ? null : h("slot", { name: aceHeroItemSlots.DESCRIPTION }))));
    }
    /**
     * @private
     */
    renderBasicHeroItem() {
        let cssClasses = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____BASIC]: true,
        };
        return (h("div", { id: this.names.ID.ROOT, class: this.names.CLASS.ROOT }, this.renderBackgroundVisual(), h("div", { class: cssClasses }, this.renderTagsSlot(), this.renderTextBlock())));
    }
    /**
     *
     * @private
     */
    renderGameHeroItem() {
        let cssClass = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____GAME]: true,
        };
        return (h("div", { id: this.names.ID.ROOT, class: this.names.CLASS.ROOT }, this.renderBackgroundVisual(), h("div", { class: cssClass }, h("div", { class: this.names.CLASS.CONTENT____GAME__LEFT_FLEX_CONTAINER }, h("div", { class: this.names.CLASS.CONTENT____GAME__VISUAL_BLOCK }, h("slot", { name: aceHeroItemSlots.IMAGE })), h("div", { class: this.names.CLASS.CONTENT____GAME__HEADLINE }, this.renderTagsSlot(), this.renderTextBlock())), h("div", { class: this.names.CLASS.CONTENT____GAME__CTA_BLOCK }, h("slot", { name: aceHeroItemSlots.CTA })))));
    }
    /**
     * @private
     */
    renderFullBackgroundHeroItem() {
        let cssClasses = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____FULL_BACKGROUND]: true,
        };
        return (h("div", { id: this.names.ID.ROOT, class: this.names.CLASS.ROOT }, this.renderBackgroundVisual(), h("div", { class: cssClasses }, this.renderTagsSlot(), this.renderTextBlock(), this.renderInfoSlot(), h("div", { class: this.names.CLASS.CONTENT____FULL_BACKGROUND__CTA_CONTROLS_WRAPPER }, h("div", { class: this.names.CLASS.CONTENT____FULL_BACKGROUND__CTA_BLOCK }, h("slot", { name: 'cta' })), this.renderControls()))));
    }
    /**
     * @private
     */
    renderEncapsulatedHeroItem() {
        const renderEncapsulatedCtaSlot = () => {
            const hasCta = !!this.hostElement.querySelector(`[slot=${aceHeroItemSlots.CTA}]`);
            if (hasCta) {
                if (isMobile()) {
                    return (h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__CTA_BLOCK }, h("slot", { name: 'cta' })));
                }
                else {
                    return (h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__CTA_CONTROLS_WRAPPER }, h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__CTA_BLOCK }, h("slot", { name: 'cta' })), this.renderControls()));
                }
            }
        };
        let cssClasses = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____ENCAPSULATED]: true,
        };
        return (h("div", { id: this.names.ID.ROOT, class: this.names.CLASS.ROOT }, !isMobile() && this.renderBackgroundVisual(), h("div", { class: cssClasses }, h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__BOX_BLOCK }, h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__CONTENT_FLEX_CONTAINER }, this.renderTagsSlot(), this.renderTextBlock()), this.renderInfoSlot(), renderEncapsulatedCtaSlot()), h("div", { class: this.names.CLASS.CONTENT____ENCAPSULATED__VISUAL_BLOCK }, isMobile() && this.renderBackgroundVisual(), h("div", { class: this.names.CLASS.FOREGROUND_IMAGE }, h("slot", { name: 'image' })), this.renderControls(true)))));
    }
    /**
     * @private
     */
    renderGeneralHeroItem() {
        const hasCta = !!this.hostElement.querySelector(`[slot=${aceHeroItemSlots.CTA}]`);
        let cssClassWrapper = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____GENERAL]: true,
        };
        return (h("div", { id: this.names.ID.ROOT, class: this.names.CLASS.ROOT }, h("div", { class: cssClassWrapper }, h("div", { class: this.names.CLASS.CONTENT____GENERAL__BOX_BLOCK }, h("div", { class: this.names.CLASS.CONTENT____GENERAL__BOX_CONTAINER }, h("div", { class: this.names.CLASS.CONTENT____GENERAL__CONTENT_FLEX_CONTAINER }, this.renderTagsSlot(), this.renderTextBlock()), this.renderInfoSlot(), hasCta ? (h("div", { class: this.names.CLASS.CONTENT____GENERAL__CTA_BLOCK }, h("slot", { name: 'cta' }))) : null), this.renderControls()), h("div", { class: this.names.CLASS.CONTENT____GENERAL__VISUAL_BLOCK }, this.renderBackgroundVisual(), h("div", { class: this.names.CLASS.FOREGROUND_IMAGE }, h("slot", { name: 'image' })), this.renderControls(true)))));
    }
    render() {
        const renderMethod = {
            'basic': this.renderBasicHeroItem,
            'game': this.renderGameHeroItem,
            'full-background': this.renderFullBackgroundHeroItem,
            'encapsulated': this.renderEncapsulatedHeroItem,
            'general': this.renderGeneralHeroItem,
        }[this.type] || this.renderGeneralHeroItem;
        return renderMethod.call(this);
    }
    static get is() { return "ace-hero-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-hero-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-hero-item.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceHeroItemType",
                    "resolved": "\"basic\" | \"encapsulated\" | \"full-background\" | \"game\" | \"general\"",
                    "references": {
                        "IAceHeroItemType": {
                            "location": "import",
                            "path": "./ace-hero-item.types",
                            "id": "src/components/ace-hero-item/ace-hero-item.types.ts::IAceHeroItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "type",
                "reflect": false
            },
            "backgroundSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If we want to include a background image we can pass the path to here."
                },
                "getter": false,
                "setter": false,
                "attribute": "background-src",
                "reflect": false
            },
            "backgroundAlt": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If we want to define an alternative text for the background image, we can pass it here.\nShould be used to describe the background image when there is no foreground image."
                },
                "getter": false,
                "setter": false,
                "attribute": "background-alt",
                "reflect": false
            },
            "backgroundPlaceholderSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If we want to define a low resolution placeholder image for the background, we can pass it here."
                },
                "getter": false,
                "setter": false,
                "attribute": "background-placeholder-src",
                "reflect": false
            },
            "backgroundColor": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If we have no image but a background color then pass it here."
                },
                "getter": false,
                "setter": false,
                "attribute": "background-color",
                "reflect": false,
                "defaultValue": "null"
            },
            "index": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Index of the current item, if it's in a ace-hero-slider component."
                },
                "getter": false,
                "setter": false,
                "attribute": "index",
                "reflect": false
            },
            "overlay": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Whether we should show the overlay below the image or not."
                },
                "getter": false,
                "setter": false,
                "attribute": "overlay",
                "reflect": false
            },
            "heroSliderKey": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Key of the hero-slider in which this component may be wrapped."
                },
                "getter": false,
                "setter": false,
                "attribute": "hero-slider-key",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "nextHeroItem",
                "name": "nextHeroItem",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "prevHeroItem",
                "name": "prevHeroItem",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "heroItemDidRender",
                "name": "heroItemDidRender",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "backgroundSrc",
                "methodName": "changeBgSrc"
            }, {
                "propName": "backgroundColor",
                "methodName": "changeBgColor"
            }];
    }
}
//# sourceMappingURL=ace-hero-item.js.map
